import React, { useState, useEffect } from 'react';
import './Rotacje.css';
import rotationsAPI from '../../api/rotations';
import shiftsAPI from '../../api/shifts';

function Rotacje({ projects = [] }) {
  const [selectedProject, setSelectedProject] = useState(projects.length > 0 ? projects[0] : '');
  const [rotations, setRotations] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRotation, setEditingRotation] = useState(null);
  const [selectedRotation, setSelectedRotation] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [rotationTitle, setRotationTitle] = useState('');
  // Usunięto showAutoFillModal, detectedPattern, hasAskedAutoFill - zastąpione przyciskiem
  const [formData, setFormData] = useState({
    title: '',
    daysPerCycle: 9
  });

  const [rotationPattern, setRotationPattern] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  
  // Stany dla modali
  const [showDeleteRotationModal, setShowDeleteRotationModal] = useState(false);
  const [rotationToDelete, setRotationToDelete] = useState(null);
  const [showClearPatternModal, setShowClearPatternModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');

  // Wczytaj rotacje dla wybranego projektu
  useEffect(() => {
    if (!selectedProject) {
      setRotations([]);
      setShifts([]);
      return;
    }

    const loadData = async () => {
      try {
        // Wczytaj rotacje z API
        const rotationsData = await rotationsAPI.getByProject(selectedProject);
        // Konwertuj format z API na format używany w komponencie
        const formattedRotations = rotationsData.map(r => ({
          id: r.id.toString(),
          title: r.title,
          rotationNumber: r.rotationNumber,
          daysPerCycle: r.daysPerCycle || 9,
          pattern: r.pattern || [],
          project: r.project || selectedProject,
          startTime: r.startTime,
          endTime: r.endTime,
          days: r.days
        }));
        setRotations(formattedRotations);

        // Wczytaj zmiany dla projektu z API
        const shiftsData = await shiftsAPI.getByProject(selectedProject);
        // Konwertuj format - jeśli to stringi, zamień na obiekty
        const formattedShifts = (shiftsData || []).map((shift, index) => {
          if (typeof shift === 'string') {
            // Stary format - tylko nazwa
            return {
              id: Date.now() + index,
              name: shift,
              startTime: '06:00',
              endTime: '14:00',
              duration: 8,
              color: '#667eea'
            };
          } else {
            // Nowy format - pełny obiekt
            return shift;
          }
        });
        setShifts(formattedShifts);
      } catch (error) {
        console.error('Błąd ładowania rotacji:', error);
        setRotations([]);
        setShifts([]);
      }
    };

    loadData();
  }, [selectedProject]);

  // Obsługa zakończenia przeciągania poza komórkami
  useEffect(() => {
    if (!isDragging) return;
    
    const handleGlobalMouseUp = () => {
      if (isDragging && dragStart && dragEnd && selectedShift) {
        handleCellMouseUp();
      }
    };
    
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isDragging, dragStart, dragEnd, selectedShift]);

  // Nasłuchuj zmian w zmianach (shifts) - teraz przez API
  useEffect(() => {
    const loadShifts = async () => {
      if (!selectedProject) return;
      try {
        const shiftsData = await shiftsAPI.getByProject(selectedProject);
        // Konwertuj format - jeśli to stringi, zamień na obiekty
        const formattedShifts = (shiftsData || []).map((shift, index) => {
          if (typeof shift === 'string') {
            return {
              id: Date.now() + index,
              name: shift,
              startTime: '06:00',
              endTime: '14:00',
              duration: 8,
              color: '#667eea'
            };
          } else {
            return shift;
          }
        });
        setShifts(formattedShifts);
      } catch (error) {
        console.error('Błąd ładowania zmian:', error);
      }
    };

    loadShifts();
    // Nasłuchuj custom event dla aktualizacji
    const handleShiftsUpdate = () => {
      loadShifts();
    };
    window.addEventListener('shifts-updated', handleShiftsUpdate);
    return () => {
      window.removeEventListener('shifts-updated', handleShiftsUpdate);
    };
  }, [selectedProject]);

  useEffect(() => {
    // Inicjalizuj wzór rotacji przy tworzeniu nowej rotacji
    if (showAddForm && !editingRotation && rotationPattern.length === 0) {
      initializePattern();
    }
  }, [showAddForm, formData.daysPerCycle]);

  const saveRotations = async (newRotations) => {
    if (!selectedProject) return;
    try {
      // Zapisz wszystkie rotacje przez API
      // Najpierw usuń wszystkie istniejące rotacje dla projektu
      const existingRotations = await rotationsAPI.getByProject(selectedProject);
      for (const rotation of existingRotations) {
        await rotationsAPI.delete(rotation.id);
      }
      
      // Teraz dodaj wszystkie rotacje
      for (const rotation of newRotations) {
        await rotationsAPI.create({
          project: selectedProject,
          title: rotation.title,
          rotationNumber: rotation.rotationNumber,
          startTime: rotation.startTime || null,
          endTime: rotation.endTime || null,
          days: rotation.days || null,
          // Pattern i daysPerCycle są przechowywane w days lub możemy dodać osobne pole
          // Dla kompatybilności, zapisz pattern jako JSON w days
          pattern: rotation.pattern,
          daysPerCycle: rotation.daysPerCycle
        });
      }
      
      setRotations(newRotations);
      // Wyślij event o aktualizacji rotacji
      window.dispatchEvent(new CustomEvent('rotationsUpdated'));
    } catch (error) {
      console.error('Błąd zapisywania rotacji:', error);
      throw error;
    }
  };

  const initializePattern = () => {
    const pattern = [];
    for (let i = 0; i < formData.daysPerCycle; i++) {
      pattern.push({
        day: i + 1,
        shifts: Array(7).fill(null) // Wszystkie dni zaczynają jako puste
      });
    }
    setRotationPattern(pattern);
  };

  const getShiftById = (shiftId) => {
    return shifts.find(s => s.id === shiftId);
  };

  const getShiftInitials = (shift) => {
    if (!shift) return '';
    if (shift === 'off') return '';
    return shift.name ? shift.name.substring(0, 2).toUpperCase() : '';
  };

  const formatDuration = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const showInfo = (message) => {
    setInfoMessage(message);
    setShowInfoModal(true);
  };

  const handleAddRotation = async () => {
    if (!formData.title.trim()) {
      showInfo('Proszę podać nazwę rotacji');
      return;
    }

    if (rotationPattern.length === 0) {
      showInfo('Proszę zdefiniować wzór rotacji');
      return;
    }

    try {
      const newRotation = {
        id: Date.now().toString(),
        title: formData.title,
        daysPerCycle: formData.daysPerCycle,
        pattern: rotationPattern,
        rotationNumber: rotations.length + 1,
        project: selectedProject
      };

      // Zapisz przez API
      await rotationsAPI.create({
        project: selectedProject,
        title: formData.title,
        rotationNumber: rotations.length + 1,
        startTime: null,
        endTime: null,
        days: null,
        pattern: rotationPattern, // Zapisz pattern
        daysPerCycle: formData.daysPerCycle
      });

      // Odśwież listę rotacji
      const rotationsData = await rotationsAPI.getByProject(selectedProject);
      const formattedRotations = rotationsData.map(r => ({
        id: r.id.toString(),
        title: r.title,
        rotationNumber: r.rotationNumber,
        daysPerCycle: formData.daysPerCycle,
        pattern: r.pattern || [],
        project: r.project || selectedProject
      }));
      setRotations(formattedRotations);
      
      // Reset formularza
      setFormData({
        title: '',
        daysPerCycle: 9
      });
      setRotationPattern([]);
      setShowAddForm(false);
      
      window.dispatchEvent(new CustomEvent('rotationsUpdated'));
    } catch (error) {
      console.error('Błąd dodawania rotacji:', error);
      showInfo('Nie udało się dodać rotacji. Spróbuj ponownie.');
    }
  };

  const handleEditRotation = (rotation) => {
    setEditingRotation(rotation);
    setFormData({
      title: rotation.title,
      daysPerCycle: rotation.daysPerCycle
    });
    setRotationPattern(rotation.pattern);
    setShowAddForm(true);
  };

  const handleUpdateRotation = async () => {
    if (!formData.title.trim()) {
      showInfo('Proszę podać nazwę rotacji');
      return;
    }

    try {
      // Zaktualizuj rotację przez API
      await rotationsAPI.update(editingRotation.id, {
        title: formData.title,
        pattern: rotationPattern,
        daysPerCycle: formData.daysPerCycle
      });

      // Odśwież listę rotacji
      const rotationsData = await rotationsAPI.getByProject(selectedProject);
      const formattedRotations = rotationsData.map(r => ({
        id: r.id.toString(),
        title: r.title,
        rotationNumber: r.rotationNumber,
        daysPerCycle: formData.daysPerCycle,
        pattern: r.pattern || [],
        project: r.project || selectedProject
      }));
      setRotations(formattedRotations);

      setShowAddForm(false);
      setEditingRotation(null);
      setFormData({
        title: '',
        daysPerCycle: 9
      });
      setRotationPattern([]);
      
      window.dispatchEvent(new CustomEvent('rotationsUpdated'));
    } catch (error) {
      console.error('Błąd aktualizacji rotacji:', error);
      showInfo('Nie udało się zaktualizować rotacji. Spróbuj ponownie.');
    }
  };

  const handleDeleteRotation = (id) => {
    setRotationToDelete(id);
    setShowDeleteRotationModal(true);
  };

  const handleDeleteRotationConfirm = async () => {
    if (rotationToDelete) {
      try {
        await rotationsAPI.delete(rotationToDelete);
        
        // Odśwież listę rotacji
        const rotationsData = await rotationsAPI.getByProject(selectedProject);
        const formattedRotations = rotationsData.map(r => ({
          id: r.id.toString(),
          title: r.title,
          rotationNumber: r.rotationNumber,
          daysPerCycle: r.daysPerCycle || 9,
          pattern: r.pattern || [],
          project: r.project || selectedProject
        }));
        setRotations(formattedRotations);
        
        if (selectedRotation?.id === rotationToDelete) {
          setSelectedRotation(null);
        }
        setShowDeleteRotationModal(false);
        setRotationToDelete(null);
        
        window.dispatchEvent(new CustomEvent('rotationsUpdated'));
      } catch (error) {
        console.error('Błąd usuwania rotacji:', error);
        showInfo('Nie udało się usunąć rotacji. Spróbuj ponownie.');
      }
    }
  };

  const handleDeleteRotationCancel = () => {
    setShowDeleteRotationModal(false);
    setRotationToDelete(null);
  };

  // Sprawdź czy tydzień jest w pełni wypełniony
  const isWeekFilled = (week) => {
    if (!week || !week.shifts) return false;
    // Sprawdź czy wszystkie dni pracy (nie 'off' i nie null) są wypełnione
    return week.shifts.every((shift, index) => {
      // Dni wolne ('off') są OK, ale dni pracy muszą mieć przypisaną zmianę
      if (shift === 'off') return true;
      return shift !== null && shift !== undefined;
    });
  };



  const handleCellClick = (dayIndex, shiftIndex) => {
    if (!selectedShift) return;
    
    // Jeśli przeciąganie jest aktywne, nie wykonuj kliknięcia
    if (isDragging) return;

    const updatedPattern = [...rotationPattern];
    const week = updatedPattern[dayIndex]; // dayIndex to indeks tygodnia
    
    if (week.shifts[shiftIndex] === 'off') {
      // Nie można przypisać zmiany do dnia wolnego
      return;
    }

    // Przypisz zmianę tylko do wybranego dnia (nie całego tygodnia)
    week.shifts[shiftIndex] = selectedShift.id;

    setRotationPattern(updatedPattern);
  };

  // Rozpocznij przeciąganie
  const handleCellMouseDown = (dayIndex, shiftIndex, e) => {
    if (!selectedShift) return;
    if (e.button !== 0) return; // Tylko lewy przycisk myszy
    
    const week = rotationPattern[dayIndex];
    if (week.shifts[shiftIndex] === 'off') return;
    
    setIsDragging(true);
    setDragStart({ dayIndex, shiftIndex });
    setDragEnd({ dayIndex, shiftIndex });
    e.preventDefault();
  };

  // Aktualizuj pozycję podczas przeciągania
  const handleCellMouseEnter = (dayIndex, shiftIndex) => {
    if (!isDragging || !dragStart) return;
    
    const week = rotationPattern[dayIndex];
    if (week.shifts[shiftIndex] === 'off') return;
    
    setDragEnd({ dayIndex, shiftIndex });
  };

  // Zakończ przeciąganie i przypisz zmiany
  const handleCellMouseUp = () => {
    if (!isDragging || !dragStart || !dragEnd || !selectedShift) {
      setIsDragging(false);
      setDragStart(null);
      setDragEnd(null);
      return;
    }

    const updatedPattern = [...rotationPattern];
    
    // Określ zakres do wypełnienia
    const startWeek = Math.min(dragStart.dayIndex, dragEnd.dayIndex);
    const endWeek = Math.max(dragStart.dayIndex, dragEnd.dayIndex);
    const startDay = Math.min(dragStart.shiftIndex, dragEnd.shiftIndex);
    const endDay = Math.max(dragStart.shiftIndex, dragEnd.shiftIndex);

    // Wypełnij wszystkie komórki w zakresie
    for (let weekIndex = startWeek; weekIndex <= endWeek; weekIndex++) {
      const week = updatedPattern[weekIndex];
      
      // Jeśli to ten sam tydzień, wypełnij tylko dni w zakresie
      if (weekIndex === startWeek && weekIndex === endWeek) {
        for (let dayIndex = startDay; dayIndex <= endDay; dayIndex++) {
          if (week.shifts[dayIndex] !== 'off') {
            week.shifts[dayIndex] = selectedShift.id;
          }
        }
      } else if (weekIndex === startWeek) {
        // Pierwszy tydzień - od startDay do końca
        for (let dayIndex = startDay; dayIndex < week.shifts.length; dayIndex++) {
          if (week.shifts[dayIndex] !== 'off') {
            week.shifts[dayIndex] = selectedShift.id;
          }
        }
      } else if (weekIndex === endWeek) {
        // Ostatni tydzień - od początku do endDay
        for (let dayIndex = 0; dayIndex <= endDay; dayIndex++) {
          if (week.shifts[dayIndex] !== 'off') {
            week.shifts[dayIndex] = selectedShift.id;
          }
        }
      } else {
        // Tygodnie pośrednie - wypełnij wszystkie dni pracy
        week.shifts.forEach((shift, index) => {
          if (shift !== 'off') {
            week.shifts[index] = selectedShift.id;
          }
        });
      }
    }

    setRotationPattern(updatedPattern);
    setIsDragging(false);
    setDragStart(null);
    setDragEnd(null);
  };

  // Przełącz dzień między wolnym a pracującym (prawy przycisk myszy)
  const handleCellToggleDayOff = (dayIndex, shiftIndex, e) => {
    e.preventDefault();
    const updatedPattern = [...rotationPattern];
    const week = updatedPattern[dayIndex];
    
    // Przełącz między 'off' (dzień wolny) a null (dzień pracujący)
    if (week.shifts[shiftIndex] === 'off') {
      week.shifts[shiftIndex] = null; // Zmień na dzień pracujący
    } else {
      week.shifts[shiftIndex] = 'off'; // Zmień na dzień wolny
    }
    
    setRotationPattern(updatedPattern);
  };

  const handleCellClear = (dayIndex, shiftIndex) => {
    const updatedPattern = [...rotationPattern];
    const day = updatedPattern[dayIndex];
    
    if (day.shifts[shiftIndex] === 'off') {
      return; // Nie można wyczyścić dnia wolnego (użyj prawy przycisk, aby przełączyć)
    }

    day.shifts[shiftIndex] = null;
    setRotationPattern(updatedPattern);
  };

  const calculateRotationDays = () => {
    if (!selectedRotation) return 0;
    // Oblicz całkowitą liczbę dni rotacji: tygodnie w roku * 7 dni w tygodniu
    return selectedRotation.daysPerCycle * 7;
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingRotation(null);
    setFormData({
      title: '',
      daysPerCycle: 9
    });
    setRotationPattern([]);
  };


  // Wyczyść cały wzór zmianowy
  const handleClearPattern = () => {
    setShowClearPatternModal(true);
  };

  const handleClearPatternConfirm = () => {
    const clearedPattern = rotationPattern.map(day => ({
      ...day,
      shifts: day.shifts.map((shiftId, index) => {
        // Zachowaj dni wolne ('off'), wyczyść tylko przypisane zmiany
        if (shiftId === 'off') return 'off';
        return null;
      })
    }));
    setRotationPattern(clearedPattern);
    setShowClearPatternModal(false);
  };

  const handleClearPatternCancel = () => {
    setShowClearPatternModal(false);
  };

  const handleViewRotation = (rotation) => {
    setSelectedRotation(rotation);
    setRotationPattern(rotation.pattern);
    setRotationTitle(rotation.title);
    setEditingTitle(false);
  };

  const handleTitleChange = async (newTitle) => {
    setRotationTitle(newTitle);
    try {
      await rotationsAPI.update(selectedRotation.id, {
        title: newTitle
      });
      
      // Odśwież listę rotacji
      const rotationsData = await rotationsAPI.getByProject(selectedProject);
      const formattedRotations = rotationsData.map(r => ({
        id: r.id.toString(),
        title: r.title,
        rotationNumber: r.rotationNumber,
        daysPerCycle: r.daysPerCycle || 9,
        pattern: r.pattern || [],
        project: r.project || selectedProject
      }));
      setRotations(formattedRotations);
      
      const updatedRotation = formattedRotations.find(r => r.id === selectedRotation.id);
      if (updatedRotation) {
        setSelectedRotation(updatedRotation);
      }
      
      window.dispatchEvent(new CustomEvent('rotationsUpdated'));
    } catch (error) {
      console.error('Błąd aktualizacji tytułu rotacji:', error);
    }
  };

  if (projects.length === 0) {
    return (
      <div className="rotacje-container">
        <div className="empty-state">
          <p>Brak dostępnych klientów. Najpierw dodaj klienta w menu Admin.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rotacje-container">
      {!selectedRotation ? (
        <>
          <div className="rotacje-header">
            <h3>Rotacje</h3>
            {!showAddForm && (
              <button 
                className="add-rotation-btn"
                onClick={() => setShowAddForm(true)}
                disabled={!selectedProject}
              >
                +
              </button>
            )}
          </div>

          {projects.length > 0 && (
            <div className="project-selector">
              <label htmlFor="project-select-rotacje">Klient:</label>
              <select
                id="project-select-rotacje"
                value={selectedProject}
                onChange={(e) => {
                  setSelectedProject(e.target.value);
                  setShowAddForm(false);
                  setEditingRotation(null);
                  setSelectedRotation(null);
                }}
              >
                {projects.map((project, idx) => (
                  <option key={idx} value={project}>{project}</option>
                ))}
              </select>
            </div>
          )}

          {showAddForm && (
            <div className="add-rotation-form">
              <h4>{editingRotation ? 'Edytuj rotację' : 'Nowa rotacja'}</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Nazwa</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="np. Rotacja 3-brygadowa"
                  />
                </div>
                <div className="form-group">
                  <label>Tygodnie w roku</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.daysPerCycle}
                    onChange={(e) => {
                      const days = parseInt(e.target.value) || 1;
                      setFormData({ ...formData, daysPerCycle: days });
                      // Reinicjalizuj wzór
                      setTimeout(() => {
                        const pattern = [];
                        for (let i = 0; i < days; i++) {
                          pattern.push({
                            day: i + 1,
                            shifts: Array(7).fill(null) // Wszystkie dni zaczynają jako puste (użytkownik sam określi które są wolne)
                          });
                        }
                        setRotationPattern(pattern);
                      }, 0);
                    }}
                  />
                </div>
              </div>
              {rotationPattern.length > 0 && (
                <div className="rotation-pattern-editor">
                  <div className="pattern-header">
                    <div className="pattern-header-left">
                      <h5>Wzór zmianowy</h5>
                      <p>Kliknij komórkę, a następnie wybierz zmianę z menu po prawej stronie. Prawy przycisk myszy przełącza dzień między wolnym a pracującym.</p>
                    </div>
                    <div className="pattern-header-actions">
                      <button 
                        className="btn-clear"
                        onClick={handleClearPattern}
                        title="Wyczyść cały wzór zmianowy"
                      >
                        Wyczyść
                      </button>
                      <button 
                        className="btn-cancel"
                        onClick={handleCancel}
                      >
                        Anuluj
                      </button>
                      <button 
                        className="btn-save"
                        onClick={editingRotation ? handleUpdateRotation : handleAddRotation}
                      >
                        {editingRotation ? 'Zapisz' : 'Dodaj'}
                      </button>
                    </div>
                  </div>
                  <div className="pattern-container">
                    {/* Nagłówki dni tygodnia */}
                    <div className="pattern-header-row-full">
                      <div className="pattern-row pattern-header-row">
                        <div className="row-number"></div>
                        {['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'So', 'Nie'].map((dayName, index) => (
                          <div key={index} className="pattern-day-header">
                            {dayName}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="pattern-content-wrapper">
                      <div className="pattern-grid">
                        {rotationPattern.map((day, dayIndex) => (
                          <div key={day.day} className="pattern-row">
                            <div className="row-number">{day.day}</div>
                            {day.shifts.map((shiftId, shiftIndex) => {
                              const shift = shiftId === 'off' ? 'off' : getShiftById(shiftId);
                              
                              // Sprawdź czy komórka jest w zakresie przeciągania
                              const isInDragRange = isDragging && dragStart && dragEnd && 
                                ((dayIndex >= Math.min(dragStart.dayIndex, dragEnd.dayIndex) && 
                                  dayIndex <= Math.max(dragStart.dayIndex, dragEnd.dayIndex)) &&
                                 (shiftIndex >= Math.min(dragStart.shiftIndex, dragEnd.shiftIndex) && 
                                  shiftIndex <= Math.max(dragStart.shiftIndex, dragEnd.shiftIndex)));
                              
                              // Jeśli to ten sam tydzień, sprawdź tylko dni
                              const isInSameWeek = dragStart && dragEnd && 
                                dragStart.dayIndex === dragEnd.dayIndex && 
                                dayIndex === dragStart.dayIndex &&
                                shiftIndex >= Math.min(dragStart.shiftIndex, dragEnd.shiftIndex) && 
                                shiftIndex <= Math.max(dragStart.shiftIndex, dragEnd.shiftIndex);
                              
                              // Jeśli różne tygodnie, sprawdź zakres
                              const isInDifferentWeeks = dragStart && dragEnd && 
                                dragStart.dayIndex !== dragEnd.dayIndex &&
                                ((dayIndex === dragStart.dayIndex && shiftIndex >= Math.min(dragStart.shiftIndex, dragEnd.shiftIndex)) ||
                                 (dayIndex === dragEnd.dayIndex && shiftIndex <= Math.max(dragStart.shiftIndex, dragEnd.shiftIndex)) ||
                                 (dayIndex > Math.min(dragStart.dayIndex, dragEnd.dayIndex) && dayIndex < Math.max(dragStart.dayIndex, dragEnd.dayIndex)));
                              
                              const isHighlighted = isDragging && (isInSameWeek || isInDifferentWeeks) && shiftId !== 'off';
                              
                              return (
                                <div
                                  key={shiftIndex}
                                  className={`pattern-cell ${shiftId === 'off' ? 'off-day' : shiftId ? 'has-shift' : 'empty'} ${isHighlighted ? 'drag-highlight' : ''}`}
                                  style={shift && shift !== 'off' ? { backgroundColor: shift.color } : {}}
                                  onMouseDown={(e) => handleCellMouseDown(dayIndex, shiftIndex, e)}
                                  onMouseEnter={() => handleCellMouseEnter(dayIndex, shiftIndex)}
                                  onMouseUp={handleCellMouseUp}
                                  onClick={() => handleCellClick(dayIndex, shiftIndex)}
                                  onContextMenu={(e) => handleCellToggleDayOff(dayIndex, shiftIndex, e)}
                                  title={shift === 'off' ? 'Dzień wolny (PPM aby zmienić na dzień pracujący)' : shift ? shift.name : 'Pusta komórka (PPM aby zmienić na dzień wolny)'}
                                >
                                  {getShiftInitials(shift)}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                      <div className="shifts-menu">
                        <p>Wybierz zmianę do przypisania:</p>
                        <div className="shifts-menu-items">
                          {shifts.map(shift => (
                            <button
                              key={shift.id}
                              className={`shift-menu-item ${selectedShift?.id === shift.id ? 'selected' : ''}`}
                              style={{ backgroundColor: shift.color }}
                              onClick={() => setSelectedShift(shift)}
                            >
                              <div className="shift-menu-initials">{getShiftInitials(shift)}</div>
                              <div className="shift-menu-name">{shift.name}</div>
                              <div className="shift-menu-duration">{formatDuration(shift.duration)}</div>
                            </button>
                          ))}
                        </div>
                        {shifts.length === 0 && (
                          <p className="no-shifts-message">
                            Brak zdefiniowanych zmian. Przejdź do sekcji "Zmiany", aby dodać zmiany.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Lista rotacji - widoczna na głównym ekranie */}
          {!showAddForm && (
            <div className="rotations-list">
              {rotations.length === 0 ? (
                <div className="empty-state">
                  <p>Brak zdefiniowanych rotacji. Dodaj pierwszą rotację, aby rozpocząć.</p>
                </div>
              ) : (
                rotations.map(rotation => (
                  <div key={rotation.id} className="rotation-item">
                    <div className="rotation-info">
                      <div className="rotation-title">{rotation.title}</div>
                      <div className="rotation-meta">
                        Rota. {rotation.rotationNumber} • {rotation.daysPerCycle} tygodnie w roku
                      </div>
                    </div>
                    <div className="rotation-actions">
                      <button 
                        className="view-btn"
                        onClick={() => handleViewRotation(rotation)}
                        title="Zobacz"
                      >
                        👁️
                      </button>
                      <button 
                        className="edit-btn"
                        onClick={() => handleEditRotation(rotation)}
                        title="Edytuj"
                      >
                        ✏️
                      </button>
                      <button 
                        className="delete-btn"
                        onClick={() => handleDeleteRotation(rotation.id)}
                        title="Usuń"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      ) : (
        <div className="rotation-view">
          <div className="rotation-view-header">
            <button 
              className="back-btn"
              onClick={() => setSelectedRotation(null)}
            >
              ←
            </button>
            <div className="rotation-view-title">
              <h3>{rotationTitle || selectedRotation.title}</h3>
              <p>{calculateRotationDays()} Dni</p>
            </div>
          </div>
          <div className="rotation-view-content">
            <div className="rotation-identifier">
              <div className="identifier-label">
                <input
                  type="text"
                  value={rotationTitle}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  onBlur={() => setEditingTitle(false)}
                  onFocus={() => setEditingTitle(true)}
                  className="rotation-title-input"
                  placeholder="Nazwa rotacji"
                />
              </div>
              <div className="identifier-value">Rota. {selectedRotation.rotationNumber}</div>
            </div>
            <div className="pattern-grid-view">
              {/* Nagłówki dni tygodnia */}
              <div className="pattern-row pattern-header-row">
                <div className="row-number"></div>
                {selectedRotation && ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'So', 'Nie'].map((dayName, index) => (
                  <div key={index} className="pattern-day-header">
                    {dayName}
                  </div>
                ))}
              </div>
              {rotationPattern.map((day, dayIndex) => (
                <div key={day.day} className="pattern-row">
                  <div className="row-number">{day.day}</div>
                  {day.shifts.map((shiftId, shiftIndex) => {
                    const shift = shiftId === 'off' ? 'off' : getShiftById(shiftId);
                    return (
                      <div
                        key={shiftIndex}
                        className={`pattern-cell ${shiftId === 'off' ? 'off-day' : shiftId ? 'has-shift' : 'empty'}`}
                        style={shift && shift !== 'off' ? { backgroundColor: shift.color } : {}}
                      >
                        {getShiftInitials(shift)}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal potwierdzenia usunięcia rotacji */}
      {showDeleteRotationModal && (
        <div className="modal-overlay" onClick={handleDeleteRotationCancel}>
          <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Potwierdź usunięcie</h3>
              <button className="modal-close" onClick={handleDeleteRotationCancel}>×</button>
            </div>
            <div style={{ padding: '32px' }}>
              <p>Czy na pewno chcesz usunąć tę rotację?</p>
              <div className="modal-actions">
                <button className="btn-cancel" onClick={handleDeleteRotationCancel}>
                  Anuluj
                </button>
                <button className="btn-delete" onClick={handleDeleteRotationConfirm}>
                  Usuń
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal potwierdzenia wyczyszczenia wzoru */}
      {showClearPatternModal && (
        <div className="modal-overlay" onClick={handleClearPatternCancel}>
          <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Potwierdź wyczyszczenie</h3>
              <button className="modal-close" onClick={handleClearPatternCancel}>×</button>
            </div>
            <div style={{ padding: '32px' }}>
              <p>Czy na pewno chcesz wyczyścić cały wzór zmianowy?</p>
              <p className="delete-warning">⚠️ Wszystkie przypisane zmiany zostaną usunięte.</p>
              <div className="modal-actions">
                <button className="btn-cancel" onClick={handleClearPatternCancel}>
                  Anuluj
                </button>
                <button className="btn-delete" onClick={handleClearPatternConfirm}>
                  Wyczyść
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal informacyjny */}
      {showInfoModal && (
        <div className="modal-overlay" onClick={() => setShowInfoModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Informacja</h3>
              <button className="modal-close" onClick={() => setShowInfoModal(false)}>×</button>
            </div>
            <div style={{ padding: '32px' }}>
              <p>{infoMessage}</p>
              <div className="modal-actions">
                <button className="btn-confirm" onClick={() => setShowInfoModal(false)}>
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Rotacje;

